import React, { useReducer, useRef, useEffect, useMemo, useCallback, useState } from 'react'
import { FixedSizeList as List, ListChildComponentProps } from 'react-window'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Search, Copy, Download, ChevronUp, ChevronDown } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from '@/i18n'
import { colors } from '@/styles/colors'
import { useUIv2 } from '@/hooks/useUIv2'
import PreviewMatchList, { DEFAULT_MAX_ROWS } from '@/components/PreviewSearch/PreviewMatchList'
import type { PreviewMatch } from '@/components/PreviewSearch/types'
import JsonWorker from './JsonViewer.worker?worker'

interface JsonViewerProps {
  data: unknown
  title?: string
  /**
   * VISN-319: when false, the Download action shows an explanatory toast and
   * does nothing — used while a placeholder or "no ground truth yet" note is
   * showing, so the note object can't be downloaded masquerading as a real
   * PARSED/GT export.
   */
  downloadable?: boolean
  /**
   * When provided, Download runs this instead of saving the on-screen JSON —
   * used by the Ground Truth mode to hand out the STORED ground truth, which is
   * the answer key, rather than the live preview a re-parse can move. Takes
   * precedence over `downloadable`: the stored copy exists independently of
   * whatever placeholder the preview is showing.
   */
  onDownloadOverride?: () => void | Promise<void>
  /**
   * When provided (V2 mode), JsonViewer hides its own search input and
   * uses this term as the source of truth. Match info flows back through
   * `onSearchMatchInfo`, and the parent drives navigation through the
   * `onSearchNext` / `onSearchPrevious` refs (same pattern as
   * EditorWrapper's canvas search).
   */
  externalSearchTerm?: string
  onSearchMatchInfo?: (info: { total: number; current: number }) => void
  onSearchNext?: React.MutableRefObject<(() => void) | null>
  onSearchPrevious?: React.MutableRefObject<(() => void) | null>
}

// --- Search state management ---

interface SearchMeta {
  matchLineIndices: number[]
  cumulativeMatchCounts: number[]
}

interface SearchState {
  searchTerm: string
  debouncedSearchTerm: string
  searchGeneration: number
  currentMatchIndex: number
  matchCount: number
  isProcessing: boolean
  searchMeta: SearchMeta
}

type SearchAction =
  | { type: 'SET_SEARCH_TERM'; term: string }
  | { type: 'DEBOUNCE_COMPLETE'; term: string }
  | { type: 'SET_MATCH_INDEX'; index: number }
  | { type: 'WORKER_RESULT'; matchCount: number; meta: SearchMeta }

const EMPTY_META: SearchMeta = { matchLineIndices: [], cumulativeMatchCounts: [] }

const INITIAL_SEARCH_STATE: SearchState = {
  searchTerm: '',
  debouncedSearchTerm: '',
  searchGeneration: 0,
  currentMatchIndex: 0,
  matchCount: 0,
  isProcessing: false,
  searchMeta: EMPTY_META,
}

function searchReducer(state: SearchState, action: SearchAction): SearchState {
  switch (action.type) {
    case 'SET_SEARCH_TERM':
      return { ...state, searchTerm: action.term, isProcessing: true }
    case 'DEBOUNCE_COMPLETE':
      return {
        ...state,
        debouncedSearchTerm: action.term,
        searchGeneration: state.searchGeneration + 1,
        currentMatchIndex: 0,
      }
    case 'SET_MATCH_INDEX':
      return { ...state, currentMatchIndex: action.index }
    case 'WORKER_RESULT':
      return { ...state, matchCount: action.matchCount, isProcessing: false, searchMeta: action.meta }
    default:
      return state
  }
}

// --- Syntax highlighting (per-line, returns React nodes) ---

function highlightLine(line: string, lightTheme = false): React.ReactNode[] {
  const TOKEN_RE =
    /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g
  const tokens: React.ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = TOKEN_RE.exec(line)) !== null) {
    if (match.index > lastIndex) {
      tokens.push(line.slice(lastIndex, match.index))
    }
    const token = match[0]
    let color: string
    if (lightTheme) {
      // V2: light-bg colors
      if (/^"/.test(token)) {
        color = /:$/.test(token) ? '#0891b2' : '#16a34a' // cyan-600 / green-600
      } else if (/true|false/.test(token)) {
        color = '#9333ea' // purple-600
      } else if (/null/.test(token)) {
        color = '#737373'
      } else {
        color = '#2563eb' // blue-600
      }
    } else {
      // Legacy: dark-bg colors
      if (/^"/.test(token)) {
        color = /:$/.test(token) ? colors.cyan[400] : colors.green[400]
      } else if (/true|false/.test(token)) {
        color = colors.purple[400]
      } else if (/null/.test(token)) {
        color = colors.gray[500]
      } else {
        color = colors.blue[400]
      }
    }
    tokens.push(
      <span key={match.index} style={{ color }}>
        {token}
      </span>
    )
    lastIndex = TOKEN_RE.lastIndex
  }

  if (lastIndex < line.length) {
    tokens.push(line.slice(lastIndex))
  }
  return tokens.length > 0 ? tokens : [line]
}

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function highlightLineWithSearch(
  line: string,
  searchTerm: string,
  globalMatchOffset: number,
  activeMatchIndex: number,
  lightTheme = false,
): { nodes: React.ReactNode[]; lineMatchCount: number } {
  const escaped = escapeRegex(searchTerm)
  const searchRe = new RegExp(`(${escaped})`, 'gi')
  const parts = line.split(searchRe)

  if (parts.length === 1) {
    return { nodes: highlightLine(line, lightTheme), lineMatchCount: 0 }
  }

  const nodes: React.ReactNode[] = []
  let matchIdx = 0

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i]
    if (!part) continue

    if (i % 2 === 1) {
      // This is a search match
      const globalIdx = globalMatchOffset + matchIdx
      const isActive = globalIdx === activeMatchIndex
      nodes.push(
        <mark
          key={`m-${i}`}
          data-match-index={globalIdx}
          className="search-match text-gray-900 dark:text-dark-text"
          style={{
            backgroundColor: colors.yellow[300],
            padding: '0 2px',
            borderRadius: 2,
            fontWeight: 'bold',
            outline: isActive ? `2px solid ${colors.blue[500]}` : 'none',
            outlineOffset: isActive ? '1px' : undefined,
          }}
        >
          {part}
        </mark>
      )
      matchIdx++
    } else {
      // Non-match segment — syntax highlight it
      const highlighted = highlightLine(part, lightTheme)
      for (let j = 0; j < highlighted.length; j++) {
        const node = highlighted[j]
        if (typeof node === 'string') {
          nodes.push(<span key={`s-${i}-${j}`}>{node}</span>)
        } else {
          nodes.push(React.cloneElement(node as React.ReactElement, { key: `s-${i}-${j}` }))
        }
      }
    }
  }

  return { nodes, lineMatchCount: matchIdx }
}

// --- Helper: find which line a global match index is on ---

function getLineForMatch(matchIndex: number, meta: SearchMeta): number {
  const { matchLineIndices, cumulativeMatchCounts } = meta
  // Binary search: find first index where cumulativeMatchCounts[i] > matchIndex
  let lo = 0
  let hi = cumulativeMatchCounts.length
  while (lo < hi) {
    const mid = (lo + hi) >>> 1
    if (cumulativeMatchCounts[mid] <= matchIndex) lo = mid + 1
    else hi = mid
  }
  return matchLineIndices[lo] ?? matchLineIndices[matchLineIndices.length - 1] ?? 0
}

function getCumulativeCountBefore(lineIndex: number, meta: SearchMeta): number {
  const { matchLineIndices, cumulativeMatchCounts } = meta
  // Binary search: find first index where matchLineIndices[i] >= lineIndex
  let lo = 0
  let hi = matchLineIndices.length
  while (lo < hi) {
    const mid = (lo + hi) >>> 1
    if (matchLineIndices[mid] < lineIndex) lo = mid + 1
    else hi = mid
  }
  if (lo < matchLineIndices.length && matchLineIndices[lo] === lineIndex) {
    return lo > 0 ? cumulativeMatchCounts[lo - 1] : 0
  }
  return lo > 0 ? cumulativeMatchCounts[lo - 1] : 0
}

// --- Constants ---

const LINE_HEIGHT = 20
const DEBOUNCE_MS = 300

// --- Component ---

export default function JsonViewer({
  data,
  title = 'JSON Data',
  downloadable = true,
  onDownloadOverride,
  externalSearchTerm,
  onSearchMatchInfo,
  onSearchNext,
  onSearchPrevious,
}: JsonViewerProps) {
  const isV2 = useUIv2()
  const isExternalSearch = externalSearchTerm !== undefined
  const { t } = useTranslation()
  const [state, dispatch] = useReducer(searchReducer, INITIAL_SEARCH_STATE)
  const { searchTerm, debouncedSearchTerm, searchGeneration, currentMatchIndex, matchCount, isProcessing, searchMeta } =
    state
  const searchInputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<List>(null)
  const workerRef = useRef<Worker | null>(null)
  const requestIdRef = useRef(0)
  const isFirstRenderRef = useRef(true)
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerHeight, setContainerHeight] = useState(400)

  const jsonString = useMemo(() => JSON.stringify(data, null, 2), [data])
  const lines = useMemo(() => jsonString.split('\n'), [jsonString])

  // Measure container height
  useEffect(() => {
    if (!containerRef.current) return
    const observer = new ResizeObserver(([entry]) => {
      setContainerHeight(entry.contentRect.height)
    })
    observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [])

  // Create worker on mount, terminate on unmount
  useEffect(() => {
    try {
      const worker = new JsonWorker()
      workerRef.current = worker

      worker.onmessage = (e: MessageEvent) => {
        const { matchLineIndices, cumulativeMatchCounts, totalMatches, requestId } = e.data
        if (requestId !== requestIdRef.current) return
        dispatch({
          type: 'WORKER_RESULT',
          matchCount: totalMatches,
          meta: { matchLineIndices, cumulativeMatchCounts },
        })
      }

      worker.onerror = () => {
        dispatch({ type: 'WORKER_RESULT', matchCount: 0, meta: EMPTY_META })
      }

      return () => {
        worker.terminate()
        workerRef.current = null
      }
    } catch {
      workerRef.current = null
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Keyboard shortcut: Cmd+F / Ctrl+F. In V2 the sub-navbar owns search and
  // handles the shortcut itself.
  useEffect(() => {
    if (isExternalSearch) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        e.preventDefault()
        searchInputRef.current?.focus()
        searchInputRef.current?.select()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isExternalSearch])

  // Debounce search term (skip mount)
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false
      return
    }
    const timer = setTimeout(() => {
      dispatch({ type: 'DEBOUNCE_COMPLETE', term: searchTerm })
    }, DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Post search to worker when debounced term changes
  useEffect(() => {
    if (!debouncedSearchTerm) {
      requestIdRef.current++ // invalidate any in-flight worker request
      dispatch({ type: 'WORKER_RESULT', matchCount: 0, meta: EMPTY_META })
      return
    }

    if (workerRef.current) {
      const currentRequestId = ++requestIdRef.current
      workerRef.current.postMessage({
        lines,
        searchTerm: debouncedSearchTerm,
        requestId: currentRequestId,
      })
    } else {
      // Fallback: synchronous search counting
      const escaped = escapeRegex(debouncedSearchTerm)
      const regex = new RegExp(escaped, 'gi')
      const matchLineIndices: number[] = []
      const cumulativeMatchCounts: number[] = []
      let total = 0
      for (let i = 0; i < lines.length; i++) {
        const matches = lines[i].match(regex)
        if (matches) {
          total += matches.length
          matchLineIndices.push(i)
          cumulativeMatchCounts.push(total)
        }
      }
      dispatch({
        type: 'WORKER_RESULT',
        matchCount: total,
        meta: { matchLineIndices, cumulativeMatchCounts },
      })
    }
  }, [debouncedSearchTerm, searchGeneration, lines])

  // Scroll to current match line
  useEffect(() => {
    if (!debouncedSearchTerm || matchCount <= 0) return
    const lineIdx = getLineForMatch(currentMatchIndex, searchMeta)
    listRef.current?.scrollToItem(lineIdx, 'center')
  }, [currentMatchIndex, matchCount, debouncedSearchTerm, searchMeta])

  // External-search bridge — keep the internal reducer in sync with the
  // search term coming from the global sub-navbar.
  useEffect(() => {
    if (!isExternalSearch) return
    if (externalSearchTerm !== searchTerm) {
      dispatch({ type: 'SET_SEARCH_TERM', term: externalSearchTerm ?? '' })
    }
  }, [isExternalSearch, externalSearchTerm, searchTerm])

  // Forward match info to the parent (e.g. sub-navbar's "1/N").
  useEffect(() => {
    if (!isExternalSearch) return
    onSearchMatchInfo?.({ total: matchCount, current: currentMatchIndex })
  }, [isExternalSearch, matchCount, currentMatchIndex, onSearchMatchInfo])

  const goToNextMatch = useCallback(() => {
    if (matchCount > 0) {
      dispatch({ type: 'SET_MATCH_INDEX', index: (currentMatchIndex + 1) % matchCount })
    }
  }, [currentMatchIndex, matchCount])

  const goToPreviousMatch = useCallback(() => {
    if (matchCount > 0) {
      dispatch({ type: 'SET_MATCH_INDEX', index: (currentMatchIndex - 1 + matchCount) % matchCount })
    }
  }, [currentMatchIndex, matchCount])

  // Expose navigation to the parent via refs (same pattern as EditorWrapper).
  useEffect(() => {
    if (!isExternalSearch) return
    if (onSearchNext) onSearchNext.current = goToNextMatch
    if (onSearchPrevious) onSearchPrevious.current = goToPreviousMatch
  }, [isExternalSearch, goToNextMatch, goToPreviousMatch, onSearchNext, onSearchPrevious])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && matchCount > 0) {
      e.preventDefault()
      goToNextMatch()
    }
  }

  // FS-657: one row per match, in global match order, so out[i] is match i and
  // picking a row is just SET_MATCH_INDEX. Built up to the list's own cap — a
  // large JSON can match tens of thousands of times.
  const matchListItems = useMemo<PreviewMatch[]>(() => {
    if (!debouncedSearchTerm || matchCount === 0) return []
    const { matchLineIndices, cumulativeMatchCounts } = searchMeta
    const out: PreviewMatch[] = []
    for (let i = 0; i < matchLineIndices.length && out.length < DEFAULT_MAX_ROWS; i++) {
      const lineIndex = matchLineIndices[i]
      const countOnLine = cumulativeMatchCounts[i] - (i > 0 ? cumulativeMatchCounts[i - 1] : 0)
      const snippet = lines[lineIndex]?.trim() ?? ''
      const locationLabel = t('board:preview_search.line_label', { line: lineIndex + 1 })
      for (let k = 0; k < countOnLine && out.length < DEFAULT_MAX_ROWS; k++) {
        out.push({ id: `${lineIndex}-${k}`, locationLabel, snippet })
      }
    }
    return out
  }, [debouncedSearchTerm, matchCount, searchMeta, lines, t])

  const matchList =
    debouncedSearchTerm && matchCount > 0 ? (
      <PreviewMatchList
        matches={matchListItems}
        activeIndex={currentMatchIndex}
        query={debouncedSearchTerm}
        onSelect={(index) => dispatch({ type: 'SET_MATCH_INDEX', index })}
        // The rows are capped at DEFAULT_MAX_ROWS, so the header must count the
        // real total rather than the rows it happens to have been handed.
        totalCount={matchCount}
        // No isSearching: this only renders once matchCount > 0, which the worker
        // sets together with isProcessing=false — the spinner could never show.
        footnote={
          matchCount > matchListItems.length
            ? t('board:preview_search.showing_first', {
                shown: matchListItems.length,
                total: matchCount,
              })
            : undefined
        }
        className="w-[264px] shrink-0 border-l border-gray-200 dark:border-dark-border"
      />
    ) : null

  const handleCopy = () => {
    navigator.clipboard.writeText(jsonString)
    toast.success(t('data_catalog:graph_editor.toasts.json_copied'))
  }

  const handleDownload = () => {
    if (onDownloadOverride) {
      void onDownloadOverride()
      return
    }
    if (!downloadable) {
      toast.info(t('graph_editor:jsonViewer.downloadUnavailable', { defaultValue: 'Nothing to download yet.' }))
      return
    }
    // The download follows the live view: in the JSON tab's "Ground Truth" mode `data` is the
    // GoldDrawing export, so name it like the Export-GT file; otherwise it's the parsed graph.
    const gtKey =
      data && typeof data === 'object' && typeof (data as { drawing_key?: unknown }).drawing_key === 'string'
        ? (data as { drawing_key: string }).drawing_key
        : null
    const filename = gtKey ? `${gtKey}.labelled.json` : `graph-data-${Date.now()}.json`
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    toast.success(t('data_catalog:graph_editor.toasts.json_downloaded'))
  }

  // Row renderer for react-window
  const LineRenderer = useCallback(
    ({ index, style }: ListChildComponentProps) => {
      const line = lines[index]

      let content: React.ReactNode[]
      if (debouncedSearchTerm && matchCount > 0) {
        const offset = getCumulativeCountBefore(index, searchMeta)
        const result = highlightLineWithSearch(line, debouncedSearchTerm, offset, currentMatchIndex, isV2)
        content = result.nodes
      } else {
        content = highlightLine(line, isV2)
      }

      return (
        <div
          style={{ ...style, paddingLeft: 16, paddingRight: 16 }}
          className="font-mono text-sm whitespace-pre"
        >
          {content}
        </div>
      )
    },
    [lines, debouncedSearchTerm, currentMatchIndex, matchCount, searchMeta, isV2]
  )

  // Listen for download/copy events from V2 sub-navbar. Search focus is no
  // longer needed: the global sub-navbar now drives the JSON search directly
  // via the externalSearchTerm prop.
  //
  // Invoke via live refs so the (once-registered) listeners always call the latest
  // handler — which closes over the current `data`/`jsonString`. Without this the
  // listener captured the first render's handler, so toggling PARSED → Ground Truth
  // still downloaded the PARSED JSON (stale closure).
  const handleDownloadRef = useRef(handleDownload)
  handleDownloadRef.current = handleDownload
  const handleCopyRef = useRef(handleCopy)
  handleCopyRef.current = handleCopy
  useEffect(() => {
    if (!isV2) return
    const handleDownloadEvent = () => handleDownloadRef.current()
    const handleCopyEvent = () => handleCopyRef.current()
    window.addEventListener('json-download', handleDownloadEvent)
    window.addEventListener('json-copy', handleCopyEvent)
    return () => {
      window.removeEventListener('json-download', handleDownloadEvent)
      window.removeEventListener('json-copy', handleCopyEvent)
    }
  }, [isV2])

  if (isV2) {
    return (
      <div className="h-full flex flex-col bg-white dark:bg-dark-card" data-selectable-table>
        {/* Internal search bar — only rendered when the parent isn't driving
            the search externally (e.g. via the global sub-navbar). */}
        {!isExternalSearch && (
          <div className="flex items-center gap-2 px-4 py-2 border-b border-[#e5e5e5] dark:border-dark-border shrink-0">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                type="text"
                placeholder={t('graph_editor:jsonViewer.searchPlaceholder', { defaultValue: 'Search in JSON... (Ctrl+F)' })}
                value={searchTerm}
                onChange={(e) => dispatch({ type: 'SET_SEARCH_TERM', term: e.target.value })}
                onKeyDown={handleKeyDown}
                onPointerDown={(e) => e.stopPropagation()}
                className="pl-10 pr-24 h-8"
              />
              {isProcessing && searchTerm && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">{t('graph_editor:jsonViewer.searching', { defaultValue: 'Searching...' })}</span>
              )}
              {!isProcessing && searchTerm && matchCount > 0 && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-muted-foreground">
                  {t('graph_editor:jsonViewer.matchCount', { current: currentMatchIndex + 1, total: matchCount, defaultValue: '{{current}} of {{total}}' })}
                </span>
              )}
              {!isProcessing && searchTerm && matchCount === 0 && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">{t('graph_editor:search.noMatches', { defaultValue: 'No matches' })}</span>
              )}
            </div>
            {searchTerm && matchCount > 0 && (
              <div className="flex gap-1">
                <Button variant="outline" size="sm" onClick={goToPreviousMatch} onPointerDown={(e) => e.stopPropagation()} className="h-8 px-2">
                  <ChevronUp className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={goToNextMatch} onPointerDown={(e) => e.stopPropagation()} className="h-8 px-2">
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Content — virtualized, no extra padding */}
        <div className="flex-1 min-h-0 flex overflow-hidden">
          <div ref={containerRef} className="flex-1 min-w-0 h-full text-gray-800 dark:text-gray-200">
            <List
              ref={listRef}
              height={containerHeight}
              itemCount={lines.length}
              itemSize={LINE_HEIGHT}
              width="100%"
              overscanCount={20}
              className="scrollbar-thin"
            >
              {LineRenderer}
            </List>
          </div>
          {matchList}
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-dark-muted" data-selectable-table>
      {/* Header */}
      <div className="border-b p-4 bg-white dark:bg-dark-card border-gray-200 dark:border-dark-border">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text">
            {title}
          </h2>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleCopy} onPointerDown={(e) => e.stopPropagation()}>
              <Copy className="w-4 h-4 mr-1" />
              {t('graph_editor:jsonViewer.copy', { defaultValue: 'Copy' })}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <Download className="w-4 h-4 mr-1" />
              {t('graph_editor:jsonViewer.download', { defaultValue: 'Download' })}
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="flex gap-2 items-center">
          <div className="relative flex-1">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500"
            />
            <Input
              ref={searchInputRef}
              type="text"
              placeholder={t('graph_editor:jsonViewer.searchPlaceholder', { defaultValue: 'Search in JSON... (Ctrl+F)' })}
              value={searchTerm}
              onChange={(e) => dispatch({ type: 'SET_SEARCH_TERM', term: e.target.value })}
              onKeyDown={handleKeyDown}
              onPointerDown={(e) => e.stopPropagation()}
              className="pl-10 pr-24"
            />
            {isProcessing && searchTerm && (
              <div
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-gray-500 dark:text-dark-text-muted"
              >
                {t('graph_editor:jsonViewer.searching', { defaultValue: 'Searching...' })}
              </div>
            )}
            {!isProcessing && searchTerm && matchCount > 0 && (
              <div
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs font-medium text-gray-500 dark:text-dark-text-muted"
              >
                {t('graph_editor:jsonViewer.matchCount', { current: currentMatchIndex + 1, total: matchCount, defaultValue: '{{current}} of {{total}}' })}
              </div>
            )}
            {!isProcessing && searchTerm && matchCount === 0 && (
              <div
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-gray-500 dark:text-dark-text-muted"
              >
                {t('graph_editor:search.noMatches', { defaultValue: 'No matches' })}
              </div>
            )}
          </div>
          {searchTerm && matchCount > 0 && (
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={goToPreviousMatch}
                onPointerDown={(e) => e.stopPropagation()}
                className="h-9 px-2"
                title={t('graph_editor:jsonViewer.previousMatch', { defaultValue: 'Previous match' })}
              >
                <ChevronUp className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={goToNextMatch}
                onPointerDown={(e) => e.stopPropagation()}
                className="h-9 px-2"
                title={t('graph_editor:jsonViewer.nextMatch', { defaultValue: 'Next match' })}
              >
                <ChevronDown className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Content — virtualized */}
      <div className="flex-1 min-h-0 flex overflow-hidden p-4">
        <div
          ref={containerRef}
          className="flex-1 min-w-0 h-full rounded-lg bg-gray-900 text-white"
        >
          <List
            ref={listRef}
            height={containerHeight}
            itemCount={lines.length}
            itemSize={LINE_HEIGHT}
            width="100%"
            overscanCount={20}
            className="scrollbar-thin"
          >
            {LineRenderer}
          </List>
        </div>
        {matchList}
      </div>
    </div>
  )
}
